import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Upload, BarChart3, ChevronRight, Image, FileAudio, Video, Sparkles, ShieldCheck, Activity, AlertTriangle, TrendingUp } from 'lucide-react';
import { motion } from 'framer-motion';
import { WebsiteHeader } from '../components/Dashboard/WebsiteHeader';
import { StatCard } from '../components/Dashboard/StatCard';
import { ScanCard } from '../components/Dashboard/ScanCard';
import { ProgressOverviewSection } from '../components/Dashboard/ProgressOverviewSection';
import { CommunicationStyleWidget } from '../components/Dashboard/CommunicationStyleWidget';
import { SubscriptionModal } from '../components/Settings/SubscriptionModal';
import { WeeklyInsightCard } from '../components/Dashboard/WeeklyInsightCard';
import { useAuth } from '../contexts/AuthContext';
import { useScans } from '../hooks/useScans';
import { useWeeklyInsights } from '../hooks/useWeeklyInsights';
import { useGoogleReviewPrompt } from '../hooks/useGoogleReviewPrompt';
import { GoogleReviewTrigger } from '../components/ToxicDetector/GoogleReviewTrigger';
import { supabase } from '../lib/supabaseClient';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
export const DashboardPage: React.FC = () => {
  const {
    user
  } = useAuth();
  const {
    scans,
    loading
  } = useScans(user?.id);
  const {
    insights
  } = useWeeklyInsights(user?.id);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [profile, setProfile] = useState<{
    is_pro: boolean;
    scan_credits: number;
  } | null>(null);
  const [loadingProStatus, setLoadingProStatus] = useState(true);
  const firstName = user?.user_metadata?.firstName || 'User';
  const currentPlan = user?.user_metadata?.subscriptionPlan || 'free';
  const isPro = profile?.is_pro || false;
  const scansCount = profile?.scan_credits || 0;
  // Determine user tier: 'pro' | 'active' (has credits) | 'free' (no credits)
  const userTier = isPro ? 'pro' : scansCount > 0 ? 'active' : 'free';
  // Calculate stats from real scans - MOVED BEFORE useEffects that reference them
  const totalScans = scans.length;
  const completedScans = scans.filter(s => s.status === 'completed');
  const toxicScans = completedScans.filter(s => s.risk_level === 'high' || s.risk_level === 'medium');
  const avgScore = completedScans.length > 0 ? Math.round(completedScans.reduce((sum, s) => sum + (s.score || 0), 0) / completedScans.length) : null;
  // Get recent scans (top 3)
  const recentScans = scans.slice(0, 3);
  // Determine pricing display
  const pricingDisplay = isPro ? 'Unlimited Active' : 'Pay per scan - $2.99';
  // Fetch profile data from profiles table
  useEffect(() => {
    async function fetchProfile() {
      if (!user?.id) return;
      try {
        const {
          data,
          error
        } = await supabase.from('profiles').select('is_pro, scan_credits').eq('id', user.id).single();
        if (error) throw error;
        setProfile(data);
      } catch (error) {
        console.error('Error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoadingProStatus(false);
      }
    }
    fetchProfile();
    // Real-time subscription for profile updates
    const channel = supabase.channel('profile-dashboard-updates').on('postgres_changes', {
      event: 'UPDATE',
      schema: 'public',
      table: 'profiles',
      filter: `id=eq.${user?.id}`
    }, payload => {
      const newProfile = payload.new as {
        is_pro: boolean;
        scan_credits: number;
      };
      setProfile(newProfile);
    }).subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);
  // Track dashboard view on mount
  useEffect(() => {
    if (!loading && !loadingProStatus && user?.id) {
      trackCriticalEvent('Dashboard_Viewed', {
        total_scans: totalScans,
        completed_scans: completedScans.length,
        is_pro: isPro,
        scans_remaining: scansCount,
        avg_toxicity_score: avgScore
      });
    }
  }, [loading, loadingProStatus, user?.id, totalScans, completedScans.length, isPro, scansCount, avgScore]);
  const fileTypes = [{
    icon: FileText,
    label: 'Text/PDF',
    color: 'text-gray-400'
  }, {
    icon: Image,
    label: 'Screenshots',
    color: 'text-gray-400'
  }, {
    icon: Video,
    label: 'Videos',
    color: 'text-gray-400'
  }, {
    icon: FileAudio,
    label: 'Audio',
    color: 'text-gray-400'
  }];
  // Google review prompt is handled by GoogleReviewContext (renders modal internally)
  useGoogleReviewPrompt();
  return <div className="bg-[#050505] min-h-screen text-white" data-id="element-4010">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none fixed" data-id="element-4011" />

      <WebsiteHeader data-id="element-4012" />

      <div className="max-w-6xl mx-auto w-full relative z-10" data-id="element-4013">
        <main className="px-4 md:px-6 pt-8 md:pt-12 pb-24 md:pb-16" data-id="element-4014">
          {/* Welcome Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="mb-10" data-id="element-4015">
            <div className="flex items-center gap-2 mb-4" data-id="element-4016">
              <span className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse" data-id="element-4017" />
              <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-4018">
                Operative Terminal Active
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-white mb-3 uppercase tracking-tight" data-id="element-4019">
              Operative: {firstName}
            </h2>
            <p className="text-gray-400 font-mono text-sm md:text-base max-w-2xl" data-id="element-4020">
              Forensic surveillance dashboard — monitor threat patterns and
              behavioral anomalies across intercepted communications.
            </p>
          </motion.div>

          {/* Upload Card - Forensic Style */}
          <Link to="/upload" className="block mb-10 group" data-id="element-4021">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.1
          }} className="relative bg-[#0a0a0a] border-2 border-gray-800 p-8 md:p-10 group-hover:border-[#FF0000]/50 transition-all duration-300" data-id="element-4022">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#FF0000]" data-id="element-4023" />
              <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#FF0000]" data-id="element-4024" />
              <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#FF0000]" data-id="element-4025" />
              <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#FF0000]" data-id="element-4026" />

              {/* Scanning Line */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-[#FF0000] to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[scan_2s_ease-in-out_infinite]" data-id="element-4027" />

              <div className="border-2 border-dashed border-gray-800 p-8 md:p-12 text-center group-hover:border-[#FF0000]/30 group-hover:bg-[#FF0000]/5 transition-all duration-300" data-id="element-4028">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF0000]/10 border border-[#FF0000] mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-4029">
                  <Upload className="w-8 h-8 text-[#FF0000]" data-id="element-4030" />
                </div>
                <h3 className="text-xl md:text-2xl font-black text-white mb-3 uppercase tracking-tight" data-id="element-4031">
                  Initialize Forensic Scan
                </h3>
                <p className="text-gray-400 mb-8 max-w-md mx-auto font-mono text-sm" data-id="element-4032">
                  Submit evidence files for AI-powered forensic analysis.
                  Supports screenshots, PDFs, and media intercepts.
                </p>

                {/* File Types Grid */}
                <div className="flex flex-wrap justify-center gap-6 opacity-50" data-id="element-4033">
                  {fileTypes.map((type, index) => {
                  const Icon = type.icon;
                  return <div key={index} className="flex items-center gap-2 text-gray-500" data-id="element-4034">
                        <Icon size={14} data-id="element-4035" />
                        <span className="text-[10px] font-mono uppercase tracking-widest" data-id="element-4036">
                          {type.label}
                        </span>
                      </div>;
                })}
                </div>
              </div>
            </motion.div>
          </Link>

          {/* Stats Grid - Forensic Style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-id="element-4037">
            <StatCard value={totalScans.toString()} label="Cases Analyzed" icon={<FileText size={20} data-id="element-4039" />} data-id="element-4038" />
            <StatCard value={toxicScans.length.toString()} label="Threat Detections" icon={<ShieldCheck size={20} data-id="element-4041" />} data-id="element-4040" />
            <StatCard value={avgScore !== null ? avgScore.toString() : '—'} label="Avg. Threat Index" icon={<Activity size={20} data-id="element-4043" />} description="Threat Scale: 0–100" data-id="element-4042" />
          </motion.div>

          {/* Weekly Insight */}
          {insights?.primaryInsight && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.25
        }} className="mb-10" data-id="element-4044">
              <WeeklyInsightCard insight={insights.primaryInsight} data-id="element-4045" />
            </motion.div>}

          {/* Progress Overview Section */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-4046">
            <ProgressOverviewSection scans={scans} loading={loading} data-id="element-4047" />
          </motion.div>

          {/* Communication Style Widget */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.35
        }} data-id="element-4048">
            <CommunicationStyleWidget scans={scans} data-id="element-4049" />
          </motion.div>

          {/* Subscription Status - Forensic Style with 3-tier display */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className={`relative bg-[#0a0a0a] border-2 p-6 md:p-8 mb-10 ${userTier === 'pro' ? 'border-[#FFD700]/50' : userTier === 'active' ? 'border-green-500/30' : 'border-gray-700'}`} data-id="element-4050">
            {/* Corner Brackets */}
            <div className={`absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-4051" />
            <div className={`absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-4052" />
            <div className={`absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-4053" />
            <div className={`absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 ${userTier === 'pro' ? 'border-[#FFD700]' : userTier === 'active' ? 'border-green-500' : 'border-gray-600'}`} data-id="element-4054" />

            <div className="flex items-center justify-between flex-wrap gap-4" data-id="element-4055">
              <div data-id="element-4056">
                <div className="flex items-center gap-3 mb-2" data-id="element-4057">
                  <p className={`text-[10px] font-mono uppercase tracking-widest ${userTier === 'pro' ? 'text-[#FFD700]' : userTier === 'active' ? 'text-green-500' : 'text-gray-500'}`} data-id="element-4058">
                    Clearance Level
                  </p>
                  {/* Tier Badge */}
                  <span className={`px-2 py-0.5 border text-[10px] font-mono font-bold uppercase tracking-widest ${userTier === 'pro' ? 'bg-[#FFD700]/10 text-[#FFD700] border-[#FFD700]/30' : userTier === 'active' ? 'bg-green-500/10 text-green-500 border-green-500/30' : 'bg-gray-800 text-gray-500 border-gray-700'}`} data-id="element-4059">
                    {userTier === 'pro' ? '★ PRO' : userTier === 'active' ? 'PAY-PER-SCAN' : 'FREE'}
                  </span>
                </div>
                <div className="flex items-baseline gap-3" data-id="element-4060">
                  {loadingProStatus ? <p className="text-2xl font-black text-white uppercase tracking-tight" data-id="element-4061">
                      Verifying...
                    </p> : userTier === 'pro' ? <p className="text-2xl font-black text-[#FFD700] uppercase tracking-tight" data-id="element-4062">
                      ∞ Unlimited Scans
                    </p> : <p className="text-2xl font-black text-white uppercase tracking-tight" data-id="element-4063">
                      Scan Credits:{' '}
                      <span className={scansCount > 0 ? 'text-green-500' : 'text-[#FF0000]'} data-id="element-4064">
                        {scansCount}
                      </span>
                    </p>}
                </div>
                {/* Tier description */}
                <p className="text-xs font-mono text-gray-500 mt-1" data-id="element-4065">
                  {userTier === 'pro' ? 'Full forensic access — all features unlocked' : userTier === 'active' ? `${scansCount} scan${scansCount !== 1 ? 's' : ''} remaining — purchase more or upgrade to Pro` : 'No scan credits — purchase a scan or upgrade to Pro'}
                </p>
              </div>
              {!isPro && !loadingProStatus && <button onClick={() => {
              trackCriticalEvent('Upgrade_CTA_Clicked', {
                cta_location: 'subscription_card',
                current_plan: userTier,
                scans_remaining: scansCount
              });
              setShowSubscriptionModal(true);
            }} className="inline-flex items-center gap-2 px-6 py-3 bg-[#FFD700] text-black font-bold uppercase tracking-wider hover:bg-[#FFD700]/90 transition-all text-sm shadow-[0_0_20px_rgba(255,215,0,0.4)]" data-id="element-4066">
                  {userTier === 'active' ? 'Upgrade to Pro' : 'Get Started'}
                  <ChevronRight size={16} data-id="element-4067" />
                </button>}
            </div>
          </motion.div>

          {/* Quick Actions - Forensic Style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.5
        }} className="mb-10" data-id="element-4068">
            <h3 className="text-lg font-black text-white mb-6 uppercase tracking-tight flex items-center gap-2" data-id="element-4069">
              <span className="w-1 h-4 bg-[#FF0000]" data-id="element-4070" />
              Command Center
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-4071">
              <Link to="/upload" className="group" data-id="element-4072">
                <div className="relative bg-[#0a0a0a] border border-gray-800 p-6 group-hover:border-[#FF0000]/50 transition-all duration-300 h-full" data-id="element-4073">
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-4074" />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-4075" />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-4076" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-4077" />

                  <div className="flex items-center gap-4" data-id="element-4078">
                    <div className="flex items-center justify-center w-12 h-12 bg-gray-800 border border-gray-700 text-gray-400 group-hover:bg-[#FF0000]/10 group-hover:border-[#FF0000] group-hover:text-[#FF0000] transition-all duration-300" data-id="element-4079">
                      <Upload size={24} data-id="element-4080" />
                    </div>
                    <div data-id="element-4081">
                      <h4 className="font-bold text-white mb-1 uppercase tracking-wide text-sm group-hover:text-[#FF0000] transition-colors" data-id="element-4082">
                        Initialize Scan
                      </h4>
                      <p className="text-xs text-gray-500 font-mono" data-id="element-4083">
                        Submit evidence for forensic analysis
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
              <Link to="/all-scans" className="group" data-id="element-4084">
                <div className="relative bg-[#0a0a0a] border border-gray-800 p-6 group-hover:border-[#FF0000]/50 transition-all duration-300 h-full" data-id="element-4085">
                  <div className="absolute top-0 left-0 w-3 h-3 border-t border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-4086" />
                  <div className="absolute top-0 right-0 w-3 h-3 border-t border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-4087" />
                  <div className="absolute bottom-0 left-0 w-3 h-3 border-b border-l border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-4088" />
                  <div className="absolute bottom-0 right-0 w-3 h-3 border-b border-r border-gray-600 group-hover:border-[#FF0000] transition-colors" data-id="element-4089" />

                  <div className="flex items-center gap-4" data-id="element-4090">
                    <div className="flex items-center justify-center w-12 h-12 bg-gray-800 border border-gray-700 text-gray-400 group-hover:bg-[#FF0000]/10 group-hover:border-[#FF0000] group-hover:text-[#FF0000] transition-all duration-300" data-id="element-4091">
                      <BarChart3 size={24} data-id="element-4092" />
                    </div>
                    <div data-id="element-4093">
                      <h4 className="font-bold text-white mb-1 uppercase tracking-wide text-sm group-hover:text-[#FF0000] transition-colors" data-id="element-4094">
                        Access Case Files
                      </h4>
                      <p className="text-xs text-gray-500 font-mono" data-id="element-4095">
                        Review forensic intelligence reports
                      </p>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </motion.div>

          {/* Recent Scans - Forensic Style */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="mb-10" data-id="element-4096">
            <div className="flex items-center justify-between mb-6" data-id="element-4097">
              <h3 className="text-lg font-black text-white uppercase tracking-tight flex items-center gap-2" data-id="element-4098">
                <span className="w-1 h-4 bg-[#FF0000]" data-id="element-4099" />
                Recent Case Files
              </h3>
              {recentScans.length > 0 && <Link to="/all-scans" className="text-sm font-mono text-[#FF0000] hover:text-[#FF0000]/80 transition-colors uppercase tracking-wide" data-id="element-4100">
                  View All →
                </Link>}
            </div>

            {loading ? <div className="space-y-4" data-id="element-4101">
                {[1, 2].map(i => <div key={i} className="bg-[#0a0a0a] border border-gray-800 p-6 animate-pulse" data-id="element-4102">
                    <div className="flex items-center gap-4" data-id="element-4103">
                      <div className="w-12 h-12 bg-gray-800" data-id="element-4104" />
                      <div className="flex-1 space-y-2" data-id="element-4105">
                        <div className="h-4 bg-gray-800 w-1/3" data-id="element-4106" />
                        <div className="h-3 bg-gray-900 w-1/4" data-id="element-4107" />
                      </div>
                    </div>
                  </div>)}
              </div> : recentScans.length === 0 ? <div className="relative bg-[#0a0a0a] border-2 border-gray-800 p-12 text-center" data-id="element-4108">
                <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600" data-id="element-4109" />
                <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600" data-id="element-4110" />
                <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600" data-id="element-4111" />
                <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600" data-id="element-4112" />

                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-800 border border-gray-700 mb-4" data-id="element-4113">
                  <FileText className="w-8 h-8 text-gray-600" data-id="element-4114" />
                </div>
                <h4 className="font-bold text-white mb-2 uppercase tracking-wide" data-id="element-4115">
                  No Cases Filed
                </h4>
                <p className="text-sm text-gray-500 mb-6 max-w-sm mx-auto font-mono" data-id="element-4116">
                  Your forensic case files will appear here. Begin by submitting
                  evidence for analysis.
                </p>
                <Link to="/upload" className="inline-flex items-center justify-center px-6 py-3 bg-[#FF0000] text-white font-bold uppercase tracking-wider hover:bg-[#FF0000]/90 transition-all shadow-[0_0_20px_rgba(255,0,0,0.4)]" data-id="element-4117">
                  Initialize First Scan
                </Link>
              </div> : <div className="space-y-4" data-id="element-4118">
                {recentScans.map(scan => <ScanCard key={scan.id} scanId={scan.id} title={scan.title} timeAgo={getTimeAgo(scan.created_at)} score={scan.score || 0} riskLevel={getRiskLevelText(scan.risk_level)} messages={scan.message_count || 0} tags={getTopPatterns(scan.patterns)} status={scan.status} patterns={scan.patterns || []} summary={(scan.analysis as any)?.summary} isUnlocked={scan.is_unlocked} data-id="element-4119" />)}
              </div>}
          </motion.div>

          {/* Upgrade CTA - Only for non-Pro users */}
          {!isPro && !loadingProStatus && <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.7
        }} className="relative bg-gradient-to-r from-[#FF0000] via-[#FF0000]/90 to-[#FF0000] border-2 border-[#FF0000] p-8 md:p-12 text-white overflow-hidden" data-id="element-4120">
              {/* Scanning Line */}
              <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-white to-transparent animate-[scan_3s_ease-in-out_infinite]" data-id="element-4121" />

              {/* Grid Overlay */}
              <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" data-id="element-4122" />

              <div className="relative z-10 text-center max-w-2xl mx-auto" data-id="element-4123">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-white/20 border border-white/30 mb-4" data-id="element-4124">
                  <Sparkles className="w-6 h-6 text-white" data-id="element-4125" />
                </div>
                <h3 className="text-2xl md:text-3xl font-black mb-3 uppercase tracking-tight text-white" data-id="element-4126">
                  Unlock Full Forensic Access
                </h3>
                <p className="text-white/90 mb-8 text-base md:text-lg font-mono" data-id="element-4127">
                  Gain unlimited forensic scans, deep behavioral pattern
                  analysis, and priority processing with Pro clearance.
                </p>
                <button onClick={() => {
              trackCriticalEvent('Upgrade_CTA_Clicked', {
                cta_location: 'dashboard_banner',
                current_plan: userTier,
                scans_remaining: scansCount
              });
              setShowSubscriptionModal(true);
            }} className="px-8 py-4 bg-white text-[#FF0000] font-bold uppercase tracking-wider hover:bg-gray-100 transition-all shadow-lg" data-id="element-4128">
                  Upgrade Clearance — 30% Off
                </button>
              </div>
            </motion.div>}
        </main>
      </div>

      {/* Subscription Modal */}
      <SubscriptionModal isOpen={showSubscriptionModal} onClose={() => setShowSubscriptionModal(false)} currentPlan={currentPlan} data-id="element-4129" />
      <GoogleReviewTrigger userId={user?.id} data-id="element-4130" />
    </div>;
};
function getTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 7)}w ago`;
}
function getRiskLevelText(riskLevel: 'low' | 'medium' | 'high' | null): string {
  if (!riskLevel) return 'Processing';
  return riskLevel === 'high' ? 'High Risk' : riskLevel === 'medium' ? 'Medium Risk' : 'Low Risk';
}
function getTopPatterns(patterns: any[] | null): string[] {
  if (!patterns || !Array.isArray(patterns) || patterns.length === 0) return [];
  return patterns.slice(0, 2).map(p => p.type || p);
}